import { API_CONFIG, fetchApi } from "./api";

const BASE_URL = API_CONFIG.POOL_SERVICE;

// Types
export interface Pool {
  id: number;
  name: string;
  location: string;
  capacity: string; // ← Corregido: capacity es string en el backend
  type: string; // ← Mantenemos string para compatibilidad con PoolType enum
  imageUrl?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PoolCreateRequest {
  name: string;
  location: string;
  capacity: string; // ← Corregido: capacity es string
  type: string;
  imageUrl?: string;
}

export interface PoolParameter {
  id: number;
  parameterId: string;
  name: string;
  unit: string;
  icon?: string;
  optimalMin: number;
  optimalMax: number;
  warningMin: number;
  warningMax: number;
  deviceType: string;
  active: boolean;
}

export interface Technician {
  id: number;
  name: string;
  email: string;
  phone?: string;
  specialty: string;
  imageUrl?: string;
  active: boolean;
}

export interface TechnicianCreateRequest {
  name: string;
  email: string;
  phone?: string;
  specialty: string;
  imageUrl?: string;
  active: boolean;
}

export interface ChemicalProduct {
  id: number;
  productId: string;
  name: string;
  unit: string;
  icon?: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ChemicalProductCreateRequest {
  productId: string;
  name: string;
  unit: string;
  icon?: string;
}

export interface ChemicalProductUpdateRequest {
  productId: string;
  name: string;
  unit: string;
  icon?: string;
}

// Helper: extrae el array ya sea de un PagedResponse o de un array directo
// El backend puede devolver cualquiera de los dos formatos
function extractContent<T>(res: PagedResponse<T> | T[]): T[] {
  if (Array.isArray(res)) return res;
  if (
    res &&
    typeof res === "object" &&
    "content" in res &&
    Array.isArray((res as PagedResponse<T>).content)
  ) {
    return (res as PagedResponse<T>).content;
  }
  return [];
}

// Pool API
export const poolApi = {
  // GET /pools retorna List<PoolResponse> directo (sin paginación)
  getAll: async (): Promise<Pool[]> => {
    const res = await fetchApi<Pool[] | PagedResponse<Pool>>(
      `${BASE_URL}/pools`,
    );
    return extractContent<Pool>(res);
  },

  // Endpoint paginado separado (/pools/paginated si existe)
  getAllPaged: (page = 0, size = 20) =>
    fetchApi<PagedResponse<Pool>>(
      `${BASE_URL}/pools?page=${page}&size=${size}`,
    ),

  getById: (id: number) => fetchApi<Pool>(`${BASE_URL}/pools/${id}`),

  getByType: (type: string) =>
    fetchApi<PagedResponse<Pool>>(`${BASE_URL}/pools/type/${type}`),

  search: (name: string) =>
    fetchApi<PagedResponse<Pool>>(
      `${BASE_URL}/pools/search?name=${encodeURIComponent(name)}`,
    ),

  create: (data: PoolCreateRequest) =>
    fetchApi<Pool>(`${BASE_URL}/pools`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: number, data: Partial<PoolCreateRequest>) =>
    fetchApi<Pool>(`${BASE_URL}/pools/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    fetchApi<void>(`${BASE_URL}/pools/${id}`, { method: "DELETE" }),
};

// Parameters API
export const parameterApi = {
  getAll: () => fetchApi<PoolParameter[]>(`${BASE_URL}/parameters`),

  getById: (id: number) =>
    fetchApi<PoolParameter>(`${BASE_URL}/parameters/${id}`),

  getByCode: (code: string) =>
    fetchApi<PoolParameter>(`${BASE_URL}/parameters/code/${code}`),

  getByDeviceType: (deviceType: string) =>
    fetchApi<PoolParameter[]>(`${BASE_URL}/parameters/device/${deviceType}`),
};

// Technicians API
export const technicianApi = {
  // GET /technicians retorna List<TechnicianResponse> directo (sin paginación)
  getAll: async (): Promise<Technician[]> => {
    const res = await fetchApi<Technician[] | PagedResponse<Technician>>(
      `${BASE_URL}/technicians`,
    );
    return extractContent<Technician>(res);
  },

  getById: (id: number) =>
    fetchApi<Technician>(`${BASE_URL}/technicians/${id}`),

  create: (data: TechnicianCreateRequest) =>
    fetchApi<Technician>(`${BASE_URL}/technicians`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: number, data: Partial<TechnicianCreateRequest>) =>
    fetchApi<Technician>(`${BASE_URL}/technicians/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    fetchApi<void>(`${BASE_URL}/technicians/${id}`, { method: "DELETE" }),
};

// Chemical Products API - CRUD completo
export const productApi = {
  // GET /inventory/products retorna List<ChemicalProductResponse> directo (sin paginación)
  getAll: async (): Promise<ChemicalProduct[]> => {
    const res = await fetchApi<
      ChemicalProduct[] | PagedResponse<ChemicalProduct>
    >(`${BASE_URL}/inventory/products`);
    return extractContent<ChemicalProduct>(res);
  },

  getById: (id: number) =>
    fetchApi<ChemicalProduct>(`${BASE_URL}/inventory/products/${id}`),

  getByCode: (code: string) =>
    fetchApi<ChemicalProduct>(`${BASE_URL}/inventory/products/code/${code}`),

  create: (data: ChemicalProductCreateRequest) =>
    fetchApi<ChemicalProduct>(`${BASE_URL}/inventory/products`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: number, data: ChemicalProductUpdateRequest) =>
    fetchApi<ChemicalProduct>(`${BASE_URL}/inventory/products/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    fetchApi<void>(`${BASE_URL}/inventory/products/${id}`, {
      method: "DELETE",
    }),
};

// Chemical Inventory interfaces - Actualizada para coincidir con el backend
export interface ChemicalInventory {
  id: number;
  active: boolean;
  chemical_product: {
    id: number;
    productId: string;
    name: string;
    unit: string;
    icon?: string;
  };
  pool: {
    id: number;
    name: string;
    location: string;
  } | null;
  current_stock: number;
  minimum_stock: number;
  maximum_stock: number;
  unit_cost: number;
  total_value: number;
  last_updated_by?: {
    id: number;
    name: string;
    email: string;
  };
  location_description?: string;
  stock_status:
    | "OUT_OF_STOCK"
    | "CRITICAL_LOW"
    | "LOW_STOCK"
    | "NORMAL"
    | "OVER_STOCK";
  stock_percentage: number;
  days_of_inventory?: number;
  created_at?: string;
  updated_at?: string;
}

export interface ChemicalInventoryCreateRequest {
  chemical_product_id: number;
  pool_id?: number;
  initial_stock: number;
  minimum_stock: number;
  maximum_stock: number;
  unit_cost: number;
  location_description?: string;
  notes?: string;
}

export interface ChemicalInventoryUpdateRequest {
  current_stock?: number;
  minimum_stock?: number;
  maximum_stock?: number;
  unit_cost?: number;
  location_description?: string;
}

export interface ChemicalUsageRequest {
  pool_id: number;
  technician_id: number;
  chemical_usages: {
    chemical_product_id: number;
    quantity_used: number;
    notes?: string;
  }[];
  measurement_reference_id?: string;
  notes?: string;
}

// Respuesta real del endpoint POST /inventory/usage
export interface UsageResponse {
  message: string;
  movements_created: number;
  pool_id: number;
  technician_id: number;
  affected_inventories: {
    product_name: string;
    previous_stock: number;
    quantity_used: number;
    new_stock: number;
    low_stock_warning: boolean;
  }[];
}

export interface ChemicalMovement {
  id: number;
  chemical_inventory_id: number;
  movement_type: "ENTRY" | "EXIT" | "ADJUSTMENT" | "TRANSFER";
  quantity: number;
  reason: string;
  notes?: string;
  previous_stock: number;
  new_stock: number;
  unit_cost: number;
  total_cost: number;
  created_at: string;
}

export interface PagedResponse<T> {
  content: T[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: {
      sorted: boolean;
      unsorted: boolean;
      empty: boolean;
    };
    offset: number;
    unpaged: boolean;
    paged: boolean;
  };
  totalPages: number;
  totalElements: number;
  last: boolean;
  first: boolean;
  numberOfElements: number;
  size: number;
  number: number;
  sort: {
    sorted: boolean;
    unsorted: boolean;
    empty: boolean;
  };
  empty: boolean;
}

// Chemical Inventory API - Corregida para coincidir con el backend
export const inventoryApi = {
  // GET all inventory with pagination
  getAll: (page = 0, size = 20, sort = "chemicalProduct.name,asc") =>
    fetchApi<PagedResponse<ChemicalInventory>>(
      `${BASE_URL}/inventory/chemicals?page=${page}&size=${size}&sort=${sort}`,
    ),

  // GET inventory by ID
  getById: (id: number) =>
    fetchApi<ChemicalInventory>(`${BASE_URL}/inventory/chemicals/${id}`),

  // GET inventory by pool (corregido: endpoint pools/{poolId}/chemicals)
  getByPool: (
    poolId: number,
    page = 0,
    size = 20,
    sort = "chemicalProduct.name,asc",
  ) =>
    fetchApi<PagedResponse<ChemicalInventory>>(
      `${BASE_URL}/inventory/pools/${poolId}/chemicals?page=${page}&size=${size}&sort=${sort}`,
    ),

  // GET low stock items (corregido: endpoint alerts/low-stock)
  getLowStock: () =>
    fetchApi<ChemicalInventory[]>(`${BASE_URL}/inventory/alerts/low-stock`),

  // GET out of stock items
  getOutOfStock: () =>
    fetchApi<ChemicalInventory[]>(`${BASE_URL}/inventory/alerts/out-of-stock`),

  // GET central warehouse inventory (corregido: endpoint central)
  getCentralWarehouse: (
    page = 0,
    size = 20,
    sort = "chemicalProduct.name,asc",
  ) =>
    fetchApi<PagedResponse<ChemicalInventory>>(
      `${BASE_URL}/inventory/central?page=${page}&size=${size}&sort=${sort}`,
    ),

  // POST create inventory item
  create: (data: ChemicalInventoryCreateRequest) =>
    fetchApi<ChemicalInventory>(`${BASE_URL}/inventory/chemicals`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // PUT update inventory item
  update: (id: number, data: ChemicalInventoryUpdateRequest) =>
    fetchApi<ChemicalInventory>(`${BASE_URL}/inventory/chemicals/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  // DELETE inventory item
  delete: (id: number) =>
    fetchApi<void>(`${BASE_URL}/inventory/chemicals/${id}`, {
      method: "DELETE",
    }),

  // POST register chemical usage — retorna UsageResponse (movements, stocks afectados)
  registerUsage: (data: ChemicalUsageRequest) =>
    fetchApi<UsageResponse>(`${BASE_URL}/inventory/usage`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // GET movement history for an inventory item (si existe en el backend)
  getMovements: (inventoryId: number, page = 0, size = 20) =>
    fetchApi<PagedResponse<ChemicalMovement>>(
      `${BASE_URL}/inventory/chemicals/${inventoryId}/movements?page=${page}&size=${size}`,
    ),

  // GET total inventory value (si existe en el backend)
  getTotalValue: () =>
    fetchApi<{ total_value: number }>(`${BASE_URL}/inventory/total-value`),

  // GET inventory summary by chemical (si existe en el backend)
  getSummary: () =>
    fetchApi<{
      total_products: number;
      low_stock_items: number;
      total_value: number;
      last_updated: string;
    }>(`${BASE_URL}/inventory/summary`),
};
